// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullAdminRegatta,
  $FullRegattaEntry,
  $Model,
} from 'common.data';

// Global Enums
import {
  GroupType,
  RegattaEntryCrewmemberGender,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Card,
  Col,
  Row,
  Table,
} from 'reactstrap';
import {
  PieChart,
} from 'react-minimal-pie-chart';
import {
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import {
  getRegattaEntryTitle,
} from 'common.helpers/dist/browser';

// Types
type $RegattaEntryWithAge = $FullRegattaEntry & {
  Age: number;
};
type $FullRegattaEntryCrewmember = $Model<'RegattaEntryCrewmember'> & {
  RegattaEntry: $FullRegattaEntry;
};
type $RegattaEntryCrewmemberWithAge = $FullRegattaEntryCrewmember & {
  Age: number;
};
type $RegattaEntryCrewmemberWithBirthday = $RegattaEntryCrewmemberWithAge & {
  AgeOnRegatta: number;
  Birthday: string;
  BirthdayWeekday: string;
  DayOfYear: number;
};
type $GenderStats = {
  female: number;
  male: number;
  unknown: number;
};
type $AgeStats = {
  regattaEntry: $RegattaEntryWithAge | null;
  regattaEntryCrewmember: $RegattaEntryCrewmemberWithAge | null;
};
type $Data = {
  birthdaysData: Array<$RegattaEntryCrewmemberWithBirthday>;
  genderData: $GenderStats;
  oldestData: $AgeStats;
  youngestData: $AgeStats;
};

type $Props = $Translate & {
  data: Array<$FullRegattaEntry>;
  id: string;
  regatta: $FullAdminRegatta;
};

const getAge = (date: string): number => moment().diff(
  moment(date),
  'years',
);

class RegattaEntriesStatsSectionView extends React.Component<$Props> {
  getPreparedData(): $Data {
    const {
      data,
      regatta,
    } = this.props;

    const dateFormat = 'YYYY-MM-DD';
    const regattaStartDateDay: number = moment(regatta.StartDate).dayOfYear();
    const regattaEndDateDay: number = moment(regatta.EndDate).dayOfYear();
    const regattaYear: number = moment(regatta.StartDate).years();

    const regattaEntryCrewmembersWithBirthdays: Array<$RegattaEntryCrewmemberWithBirthday> = [];
    const genderData: $GenderStats = {
      female: 0,
      male: 0,
      unknown: 0,
    };
    const youngestData: {
      regattaEntry: $RegattaEntryWithAge | null;
      regattaEntryCrewmember: $RegattaEntryCrewmemberWithAge | null;
    } = {
      regattaEntry: null,
      regattaEntryCrewmember: null,
    };

    const oldestData: {
      regattaEntry: $RegattaEntryWithAge | null;
      regattaEntryCrewmember: $RegattaEntryCrewmemberWithAge | null;
    } = {
      regattaEntry: null,
      regattaEntryCrewmember: null,
    };

    /* eslint-disable-next-line complexity */
    data.forEach((regattaEntry: $FullRegattaEntry) => {
      const birthdaysQuantity: number = regattaEntry.RegattaEntryCrewmembers.filter(({
        Birthdate,
      }) => Birthdate && getAge(Birthdate) > 0).length;

      if (regattaEntry.RegattaEntryCrewmembers.length === birthdaysQuantity) {
        const regattaEntryAges: Array<number> = regattaEntry.RegattaEntryCrewmembers.map(({
          Birthdate,
        }) => getAge(Birthdate || ''));

        const regattaEntryWithAge: $RegattaEntryWithAge = {
          ...regattaEntry,
          Age: _.mean(regattaEntryAges),
        };

        if (youngestData.regattaEntry !== null) {
          if (youngestData.regattaEntry.Age > regattaEntryWithAge.Age) {
            youngestData.regattaEntry = regattaEntryWithAge;
          }
        } else {
          _.set(
            youngestData,
            'regattaEntry',
            regattaEntryWithAge,
          );
        }

        if (oldestData.regattaEntry !== null) {
          if (oldestData.regattaEntry.Age < regattaEntryWithAge.Age) {
            oldestData.regattaEntry = regattaEntryWithAge;
          }
        } else {
          _.set(
            oldestData,
            'regattaEntry',
            regattaEntryWithAge,
          );
        }
      }

      /* eslint-disable-next-line complexity */
      regattaEntry.RegattaEntryCrewmembers.forEach((
        regattaEntryCrewmember: $Model<'RegattaEntryCrewmember'>,

      ) => {
        const {
          Birthdate,
          Gender,
        } = regattaEntryCrewmember;

        if (Gender) {
          if (Gender === RegattaEntryCrewmemberGender.male) {
            genderData.male += 1;
          }

          if (Gender === RegattaEntryCrewmemberGender.female) {
            genderData.female += 1;
          }
        } else {
          genderData.unknown += 1;
        }

        if (Birthdate) {
          const regattaEntryCrewmemberBirthdateDay: number = moment(
            Birthdate,
            dateFormat,
          ).dayOfYear();

          const regattaEntryCrewmemberWithAge = {
            ...regattaEntryCrewmember,
            Age: getAge(Birthdate),
            AgeOnRegatta: regattaYear - moment(Birthdate).years(),
            RegattaEntry: regattaEntry,
          };

          if (regattaEntryCrewmemberBirthdateDay >= regattaStartDateDay
            && regattaEntryCrewmemberBirthdateDay <= regattaEndDateDay
          ) {
            const birthdayMoment: moment.Moment = moment(Birthdate).set(
              'year',
              regattaYear,
            );

            const regattaEntryCrewmembersWithBirthday = {
              ...regattaEntryCrewmemberWithAge,
              Birthday: birthdayMoment.format(dateFormat),
              BirthdayWeekday: birthdayMoment.format('dddd'),
              DayOfYear: regattaEntryCrewmemberBirthdateDay,
              RegattaEntry: regattaEntry,
            };

            regattaEntryCrewmembersWithBirthdays.push(regattaEntryCrewmembersWithBirthday);
          }

          if (regattaEntryCrewmemberWithAge.Age > 0) {
            if (youngestData.regattaEntryCrewmember !== null) {
              if (youngestData.regattaEntryCrewmember.Age > regattaEntryCrewmemberWithAge.Age) {
                youngestData.regattaEntryCrewmember = regattaEntryCrewmemberWithAge;
              }
            } else {
              _.set(
                youngestData,
                'regattaEntryCrewmember',
                regattaEntryCrewmemberWithAge,
              );
            }

            if (oldestData.regattaEntryCrewmember !== null) {
              if (oldestData.regattaEntryCrewmember.Age < regattaEntryCrewmemberWithAge.Age) {
                oldestData.regattaEntryCrewmember = regattaEntryCrewmemberWithAge;
              }
            } else {
              _.set(
                oldestData,
                'regattaEntryCrewmember',
                regattaEntryCrewmemberWithAge,
              );
            }
          }
        }
      });
    });

    const filteredBirthdaysData: Array<$RegattaEntryCrewmemberWithBirthday> = _.filter(
      regattaEntryCrewmembersWithBirthdays,
      (
        regattaEntryCrewmember: $RegattaEntryCrewmemberWithBirthday,
      ) => regattaEntryCrewmember.Age > 0,
    );

    const sortedBirthdaysData: Array<$RegattaEntryCrewmemberWithBirthday> = _.sortBy(
      filteredBirthdaysData,
      'DayOfYear',
    );

    return {
      birthdaysData: sortedBirthdaysData,
      genderData,
      oldestData,
      youngestData,
    };
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    return (
      <div className={'bg-white rounded border-thin-bottom'}>
        <SectionHeader
          bottomBorder
          iconClass={'icon-chart'}
          title={t('Regatta entry crewmember statistics')}
        />
      </div>
    );
  }

  renderRegattaEntry(regattaEntry: $FullRegattaEntry | $RegattaEntryWithAge) {
    const {
      regatta,
      t,
    } = this.props;

    return (
      <div title={t('Regatta entry')}>
        {(regattaEntry.RegattaGroup && regattaEntry.RegattaGroup.Group.Type === GroupType.sport) ? (
          <>
            <div>
              {getRegattaEntryTitle({
                bowNoEnabled: regatta.BowNoEnabled,
                countryEnabled: regatta.CountryEnabled,
                regattaEntry,
              })}
            </div>
            <small className={'text-muted'}>
              {regattaEntry.RegattaGroup.Group.Name}
            </small>
          </>
        ) : (
          <>
            <div>
              {regattaEntry.Name}
            </div>
            <small className={'text-muted'}>
              {getRegattaEntryTitle({
                bowNoEnabled: regatta.BowNoEnabled,
                countryEnabled: regatta.CountryEnabled,
                regattaEntry,
              })}
            </small>
          </>
        )}
      </div>
    );
  }

  renderGenderStats(data: $GenderStats) {
    const {
      t,
    } = this.props;

    const color = {
      female: '#ffb0d4',
      male: '#9aceff',
      unknown: '#ccc',
    };

    return (
      <div className={'p-2'}>
        <div className={'mb-3'}>
          <PieChart
            data={[{
              color: color.female,
              title: t('Female'),
              value: data.female,
            }, {
              color: color.male,
              title: t('Male'),
              value: data.male,
            }, {
              color: color.unknown,
              title: t('Unknown'),
              value: data.unknown,
            }]}
            label={({
              dataEntry,
            }) => dataEntry.value || ''}
            labelPosition={70}
            labelStyle={{
              color: '#909FA7',
              fontSize: '80%',
            }}
          />
        </div>
        <div className={'d-flex flex-row'}>
          <div className={'mr-2'}>
            <em
              className={'far fa-solid fa-circle mr-1'}
              style={{
                color: color.male,
              }}
            />
            {t('Male')}
          </div>
          <div className={'mr-2'}>
            <em
              className={'far fa-solid fa-circle mr-1'}
              style={{
                color: color.female,
              }}
            />
            {t('Female')}
          </div>
          <div className={'mr-2'}>
            <em
              className={'far fa-solid fa-circle mr-1'}
              style={{
                color: color.unknown,
              }}
            />
            {'?'}
          </div>
        </div>
      </div>
    );
  }

  renderYoungestRegattaEntryCrewmember(
    youngestRegattaEntryCrewmember: $RegattaEntryCrewmemberWithAge | null,
  ) {
    const {
      t,
    } = this.props;

    return (
      <Table responsive>
        <thead>
          <tr>
            <th>
              {t('Youngest crewmember')}
            </th>
            <th>
              {t('Entry')}
            </th>
            <th className={'text-right'}>
              {t('Age')}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            {youngestRegattaEntryCrewmember !== null ? (
              <>
                <td className={'lh-1'}>
                  <div>
                    {youngestRegattaEntryCrewmember.Name}
                  </div>
                  <small className={'text-muted'}>
                    {youngestRegattaEntryCrewmember.Birthdate}
                  </small>
                </td>
                <td className={'lh-1'}>
                  {this.renderRegattaEntry(youngestRegattaEntryCrewmember.RegattaEntry)}
                </td>
                <td className={'text-right'}>
                  {youngestRegattaEntryCrewmember.Age}
                </td>
              </>
            ) : (
              <>
                <td>{'-'}</td>
                <td>{'-'}</td>
                <td className={'text-right'}>{'-'}</td>
              </>
            )}
          </tr>
        </tbody>
      </Table>
    );
  }

  renderYoungestRegattaEntry(
    youngestRegattaEntry: $RegattaEntryWithAge | null,
  ) {
    const {
      t,
    } = this.props;

    return (
      <Table responsive>
        <thead>
          <tr>
            <th>
              {t('Mean youngest entry')}
            </th>
            <th>
              {t('Crewmembers')}
            </th>
            <th className={'text-right'}>
              {t('Mean age')}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            {youngestRegattaEntry !== null ? (
              <>
                <td className={'lh-1'}>
                  {this.renderRegattaEntry(youngestRegattaEntry)}
                </td>
                <td>
                  {youngestRegattaEntry.RegattaEntryCrewmembers.length}
                </td>
                <td className={'text-right'}>
                  {youngestRegattaEntry.Age.toFixed(1)}
                </td>
              </>
            ) : (
              <>
                <td>{'-'}</td>
                <td>{'-'}</td>
                <td className={'text-right'}>{'-'}</td>
              </>
            )}
          </tr>
        </tbody>
      </Table>
    );
  }

  renderOldestRegattaEntryCrewmember(
    oldestRegattaEntryCrewmember: $RegattaEntryCrewmemberWithAge | null,
  ) {
    const {
      t,
    } = this.props;

    return (
      <Table responsive>
        <thead>
          <tr>
            <th>
              {t('Oldest crewmember')}
            </th>
            <th>
              {t('Entry')}
            </th>
            <th className={'text-right'}>
              {t('Age')}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            {oldestRegattaEntryCrewmember !== null ? (
              <>
                <td className={'lh-1'}>
                  <div>
                    {oldestRegattaEntryCrewmember.Name}
                  </div>
                  <small className={'text-muted'}>
                    {oldestRegattaEntryCrewmember.Birthdate}
                  </small>
                </td>
                <td className={'lh-1'}>
                  {this.renderRegattaEntry(oldestRegattaEntryCrewmember.RegattaEntry)}
                </td>
                <td className={'text-right'}>
                  {oldestRegattaEntryCrewmember.Age}
                </td>
              </>
            ) : (
              <>
                <td>{'-'}</td>
                <td>{'-'}</td>
                <td className={'text-right'}>{'-'}</td>
              </>
            )}
          </tr>
        </tbody>
      </Table>
    );
  }

  renderOldestRegattaEntry(
    oldestRegattaEntry: $RegattaEntryWithAge | null,
  ) {
    const {
      t,
    } = this.props;

    return (
      <Table responsive>
        <thead>
          <tr>
            <th>
              {t('Mean oldest entry')}
            </th>
            <th>
              {t('Crewmembers')}
            </th>
            <th className={'text-right'}>
              {t('Mean age')}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            {oldestRegattaEntry !== null ? (
              <>
                <td className={'lh-1'}>
                  {this.renderRegattaEntry(oldestRegattaEntry)}
                </td>
                <td>
                  {oldestRegattaEntry.RegattaEntryCrewmembers.length}
                </td>
                <td className={'text-right'}>
                  {oldestRegattaEntry.Age.toFixed(1)}
                </td>
              </>
            ) : (
              <>
                <td>{'-'}</td>
                <td>{'-'}</td>
                <td className={'text-right'}>{'-'}</td>
              </>
            )}
          </tr>
        </tbody>
      </Table>
    );
  }

  renderBirthdays(data: Array<$RegattaEntryCrewmemberWithBirthday>) {
    const {
      t,
    } = this.props;

    return (
      <Table responsive>
        <thead>
          <tr>
            <th>
              {t('Birthday during regatta')}
            </th>
            <th>
              {t('Name')}
            </th>
            <th>
              {t('Entry')}
            </th>
            <th className={'text-right'}>
              {t('Age on birthday')}
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $RegattaEntryCrewmemberWithBirthday) => (
            <tr key={dataItem.id}>
              <td className={'lh-1 py-1'}>
                <div title={t('Birthdate')}>
                  {dataItem.Birthdate}
                </div>
                <small
                  className={'text-muted'}
                  title={t('Birthday')}
                >
                  {`${dataItem.Birthday}, ${dataItem.BirthdayWeekday}`}
                </small>
              </td>
              <td>
                {dataItem.Name}
              </td>
              <td className={'lh-1 py-1'}>
                {this.renderRegattaEntry(dataItem.RegattaEntry)}
              </td>
              <td className={'text-right'}>
                {dataItem.AgeOnRegatta}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderData() {
    const {
      t,
    } = this.props;

    const {
      birthdaysData,
      genderData,
      oldestData,
      youngestData,
    } = this.getPreparedData();

    const dataQuantity: number = genderData.female + genderData.male + genderData.unknown;

    return dataQuantity > 0 ? (
      <div
        className={'pt-3'}
        style={{
          backgroundColor: 'rgba(200,200,200,0.2)',
        }}
      >
        <Row>
          <Col lg={2}>
            <Card className={'mb-0'}>
              {this.renderGenderStats(genderData)}
            </Card>
          </Col>
          <Col>
            <Row>
              <Col sm={6}>
                <Card>
                  {this.renderYoungestRegattaEntryCrewmember(youngestData.regattaEntryCrewmember)}
                </Card>
              </Col>
              <Col sm={6}>
                <Card>
                  {this.renderOldestRegattaEntryCrewmember(oldestData.regattaEntryCrewmember)}
                </Card>
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <Card className={'mb-0'}>
                  {this.renderYoungestRegattaEntry(youngestData.regattaEntry)}
                </Card>
              </Col>
              <Col sm={6}>
                <Card className={'mb-0'}>
                  {this.renderOldestRegattaEntry(oldestData.regattaEntry)}
                </Card>
              </Col>
            </Row>
            {birthdaysData.length > 0 && (
              <Card className={'mt-3 mb-0'}>
                {this.renderBirthdays(birthdaysData)}
              </Card>
            )}
          </Col>
        </Row>
      </div>
    ) : (
      <div className={'text-muted text-center p-3'}>
        {t('No data')}
      </div>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderData()}
      </Card>
    );
  }
}

export default RegattaEntriesStatsSectionView;
